<template>
  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAddSite && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />

  <div class="w-full dark:text-gray-50 flex justify-center pt-10" v-if="canAddSite || user?.is_staff">
    <div id="site-progress-tracker" class="relative w-[80%] my-4"
      v-if="selectedConfig === 'Central Monitoring Configuration' || selectedConfig === 'Probe Site Configuration'"
    >
      <!-- Track Background -->
      <div class="absolute top-5 left-0 right-0 h-2 transform -translate-y-1/2 bg-gray-200 dark:bg-gray-700 rounded-full z-20"></div>

      <!-- Progress Bar -->
      <div
        class="absolute top-5 left-0 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full z-30 transition-all duration-700 ease-out transform -translate-y-1/2"
        :style="{
          width: ((progress) / (enabledModules.length - 1) * 100) + '%'
        }"
      ></div>

      <!-- Step Circles -->
      <div class="relative flex justify-between items-center w-full">
        <template v-for="(step, index) in enabledModules" :key="index">
          <div class="flex flex-col items-center relative group">
            
            <!-- Step Circle -->
            <div
              class="relative w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all duration-500 z-40 transform group-hover:scale-110"
              :class="{
                'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg': progress > index,
                'bg-green-500 text-white shadow-xl ring-4 ring-green-500/30': progress === index,
                'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-600': progress <= index
              }"
            >
              <!-- Checkmark for completed steps -->
              <svg
                v-if="progress > index"
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
              </svg>
              <span v-else>{{ index + 1 }}</span>
            </div>
            
            <!-- Step Label -->
            <div
              class="mt-10 absolute text-xs font-medium text-center transition-all duration-300 whitespace-nowrap z-30"
              :class="{
                'text-green-600 dark:text-green-400 font-semibold': progress >= index,
                'text-gray-500 dark:text-gray-400': progress < index
              }"
            >
              {{ step }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>


  <div v-if="configSelection && (canAddSite || user?.is_staff)">
    <div
      class="flex items-center justify-center inset-0 absolute "
      >
      <div
        class=" p-6 glass-card mb-2 w-[60%] min-h-[30%] z-50 space-y-5">
        <div class="flex justify-between items-center">
          <h1 class="dark:text-white font-semibold text-gray-900 text-xl tracking-tight font-sans">Site Configuration</h1>
          <div class="relative group">
          <button type="button" class="hover:bg-transparent bg-transparent" @click="startTutorial">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
            <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
          </div>
        </div>
        <div>
          <h1 class="text-center dark:text-white font-medium text-gray-900">Organisation</h1>
          <GlassSelect
            v-model="selectedOrg"
            id="site-config-org"
            :options="orgOptions"
            :searchable="true"
            placeholder="Select an organisation"
            @focus="clearFieldError('selectedOrg')"
            :error="!!fieldErrors['selectedOrg']"
          />
          <FieldError :message="fieldErrors['selectedOrg']" />
        </div>
        <div>
          <h1 class="text-center dark:text-white font-medium text-gray-900">Site Configuration</h1>
          <GlassSelect
            v-model="selectedConfig"
            id="site-config-type"
            :options="configOptions"
            placeholder="Select a site configuration"
            @focus="clearFieldError('selectedConfig')"
            :error="!!fieldErrors['selectedConfig']"
          />
          <FieldError :message="fieldErrors['selectedConfig']" />
        </div>
        <div class="text-right space-x-2">
          <button
            @click="$router.push({ name: 'sites' })" 
            type="button" 
            class="btn bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="manageConfig" 
            type="button" 
            class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="showSite && (canAddSite || user?.is_staff)" class="p-6 min-h-full dark:text-gray-50 pt-5">
    <div class="flex items-center space-x-3 mb-6">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Site - {{ selectedOrg }}</h1>
      <div class="relative group">
      <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>
    <form @submit.prevent="addSite" class="space-y-6 p-6 glass-card">
      <!-- Required Fields Section -->
      <div class="bg-white dark:bg-gray-800 dark:text-bg-gray dark:border-gray-800 p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-50">Required Information</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <!-- Site Code -->
          <div class="flex flex-col">
            <label for="code" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Site Code<span class="text-red-500">*</span>:</label>
            <input 
              v-model="newSite.code" 
              id="code" 
              placeholder="Enter site code"
              @focus="clearFieldError('code')"
              :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['code']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['code']" />
          </div>

          <!-- Description -->
          <div class="flex flex-col">
            <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Description<span class="text-red-500">*</span>:</label>
            <input 
              v-model="newSite.description" 
              id="description" 
              placeholder="Full name of the site location"
              @focus="clearFieldError('description')"
              :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['description']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['description']" />
          </div>

          <!-- Country -->
          <div class="flex flex-col">
            <label for="country" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Country<span class="text-red-500">*</span>:</label>
            <GlassSelect
              v-model="newSite.country"
              id="country"
              :options="countryOptions"
              placeholder="Select a country"
              @change="filterRegions"
              @focus="clearFieldError('country')"
              :error="!!fieldErrors['country']"
            />
            <FieldError :message="fieldErrors['country']" />
          </div>

          <!-- Region -->
          <div class="flex flex-col">
            <label for="region" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Region<span class="text-red-500">*</span>:</label>
            <GlassSelect
              v-model="newSite.region"
              id="region"
              :options="filteredRegions"
              :searchable="true"
              placeholder="Select a region"
              :valueKey="'code'"
              :labelKey="'name'"
              :disabled="!newSite.country"
              @focus="clearFieldError('region')"
              :error="!!fieldErrors['region']"
            />
            <FieldError :message="fieldErrors['region']" />
          </div>

          <!-- Organization -->
          <div class="flex flex-col">
            <label for="org" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Organisation<span class="text-red-500">*</span>:</label>
            <GlassSelect
              v-model="newSite.org"
              id="org"
              :options="configOrganizations.filter(Boolean)"
              placeholder="Select an organisation"
              :disabled="true"
            />
          </div>

          <!-- Division -->
          <div class="flex flex-col">
            <label for="division" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Division<span class="text-red-500">*</span>:</label>
            <GlassSelect
              v-model="newSite.division"
              id="division"
              :options="orgDivisions"
              :searchable="true"
              placeholder="Select a division"
              @focus="clearFieldError('division')"
              :error="!!fieldErrors['division']"
            />
            <FieldError :message="fieldErrors['division']" />
          </div>

          <!-- Management IP Address -->
          <div class="flex flex-col">
            <label for="mgmt_loopback_address" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Management IP Address<span class="text-red-500">*</span>:</label>
            <input 
              v-model="newSite.mgmt_loopback_address" 
              id="mgmt_loopback_address" 
              type="text"
              placeholder="Enter IP address"
              @focus="clearFieldError('mgmt_loopback_address')"
              :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['mgmt_loopback_address']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['mgmt_loopback_address']" />
          </div>
        </div>
      </div>

      <!-- Optional Fields Section -->
      <div class="bg-white dark:bg-gray-800 dark:text-bg-gray dark:border-gray-800 p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-50">Optional Information</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <!-- API Address -->
          <div class="flex flex-col">
            <label for="api_address" class="mb-1 font-medium text-gray-700 dark:text-gray-50">API Address<span class="text-red-500"></span>:</label>
            <input 
              v-model="newSite.api_address" 
              id="api_address" 
              type="text"
              placeholder="Enter API address"
              :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['api_address']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['api_address']" />
          </div>

          <!-- Management IP Address Port -->
          <div class="flex flex-col">
            <label for="mgmt_loopback_address_port" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Management IP Address Port:</label>
            <input 
              v-model="newSite.mgmt_loopback_address_port" 
              id="mgmt_loopback_address_port" 
              type="number"
              min="1"
              max="65535"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- API Port -->
          <div class="flex flex-col">
            <label for="api_port" class="mb-1 font-medium text-gray-700 dark:text-gray-50">API Port:</label>
            <input 
              v-model="newSite.api_port" 
              id="api_port" 
              type="number"
              min="1"
              max="65535"
              placeholder="Enter API Port"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- API Key -->
          <div class="flex flex-col">
            <label for="api_key" class="mb-1 font-medium text-gray-700 dark:text-gray-50">API Key:</label>
            <input 
              v-model="newSite.api_key" 
              id="api_key" 
              type="password"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter API Key"
            />
          </div>

          <!-- Device Name -->
          <div class="flex flex-col">
            <label for="device_name" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Device Name:</label>
            <input 
              v-model="newSite.device_name" 
              id="device_name" 
              type="text"
              placeholder="Enter Device Name"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- Geohash -->
          <div class="flex flex-col">
            <label for="geohash" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Geohash:</label>
            <input
              v-model="newSite.geohash"
              id="geohash"
              type="text"
              placeholder="Enter Geohash"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- Scrape Interval -->
          <div v-if="user?.is_staff" class="flex flex-col">
            <label for="scrape_interval" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Scrape Interval:</label>
            <input
              v-model="newSite.scrape_interval"
              id="scrape_interval"
              type="number"
              min="1"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- Scrape Timeout -->
          <div v-if="user?.is_staff" class="flex flex-col">
            <label for="scrape_timeout" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Scrape Timeout:</label>
            <input
              v-model="newSite.scrape_timeout"
              id="scrape_timeout"
              type="number"
              min="1"
              class="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <!-- In Maintenance -->
          <div class="flex items-center mt-6">
            <input 
              v-model="newSite.in_maintenance" 
              id="in_maintenance" 
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="in_maintenance" class="ml-2 font-medium text-gray-700 dark:text-gray-50">In Maintenance</label>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex space-x-3 justify-end">
        <button
          @click="$router.push({ name: 'sites' })"
          class="btn bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Cancel
        </button>
        <button
          id="submit-site-btn"
          type="submit"
          class="btn bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Add Site
        </button>
      </div>
    </form>
  </div>
  <AddTunnelView 
    v-else-if="showTunnel && enabledModules.includes('Tunnel')"
    :isMultiSubmit="true"
    :multiSubmitOrg="newSite.org"
    :newmulti-submit-site="submittedSite.id"
    :submitted-site="submittedSite"
    @tunnel-submitted = handleNextModule
  />
  <AddProbe
    v-else-if="showProbe && enabledModules.includes('Probe')"
    :isMultiSubmit="true"
    :newmulti-submit-site="submittedSite.id"
    :submitted-site="submittedSite"
    @probe-submitted = handleNextModule
  />
  <AddBlackbox
    v-else-if="showBlackbox && enabledModules.includes('Endpoint Availability')"
    :isMultiSubmit="true"
    :newMultiSubmitSite="submittedSite.id"
    :newMultiSubmitProbe="submittedProbe?.id"
    :submitted-site="submittedSite"
    :submitted-probe="submittedProbe"
    @blackbox-submitted = handleNextModule
    @blackbox-skipped = handleNextModule
  />
  <AddHostAvailability
    v-else-if="showHostAvailability && enabledModules.includes('Host Availability')"
    :isMultiSubmit="true"
    :submitted-site="submittedSite"
    @host-availability-submitted="handleNextModule"
    @host-availability-skipped="handleNextModule"
  />
  <AddNetworkDevice
    v-else-if="showNetworkDevice && enabledModules.includes('Network Device')"
    :isMultiSubmit="true"
    :submitted-site="submittedSite"
    :submitted-probe="submittedProbe"
    @network-device-submitted="handleNextModule"
    @network-device-skipped="handleNextModule"
  />
  <AddFortigate
    v-else-if="showFortigate && enabledModules.includes('Fortigate')"
    :isMultiSubmit="true"
    :newmulti-submit-site="submittedSite.id"
    :submitted-site="submittedSite"
    @fortigate-submitted = handleNextModule
    @fortigate-skipped = handleNextModule
  />
  <AddLoadshedding
    v-else-if="showLoadsheddingarea && enabledModules.includes('Loadsheddingarea')"
    :isMultiSubmit="true"
    :newmulti-submit-site="submittedSite.id"
    :submitted-site="submittedSite"
    @loadshedding-submitted = handleNextModule
    @loadshedding-skipped = handleNextModule
  />
</template>

<script>
import { addSite } from "@/services/siteService";
import { getAllOrgs } from "@/services/orgService";
import { getAllDivisions } from "@/services/divisionService";
import FieldError from "@/components/FieldError.vue";
import AddTunnelView from "./AddTunnelView.vue";
import AddProbe from "./AddProbe.vue";
import AddBlackbox from "./AddBlackbox.vue";
import AddLoadshedding from "./AddLoadshedding.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import { getAllModules } from "@/services/moduleService";
import { isStaffOrServiceProvider } from "@/utils/schemaAccess";
import { useTutorial } from "@/composables/tutorial";
import { useMultiTenant } from "@/composables/useMultiTenant";
import AddFortigate from "./AddFortigate.vue";
import AddHostAvailability from "./AddHostAvailability.vue";
import AddNetworkDevice from "./AddNetworkDevice.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "AddSiteView",
  components: {
    FieldError,
    AddTunnelView,
    AddProbe,
    AddBlackbox,
    AddFortigate,
    AddHostAvailability,
    AddNetworkDevice,
    AddLoadshedding,
    PageError,
    GlassSelect,
  },
  data() {
    return {
      submittedSite: null,
      submittedProbe: null,
      currentModuleIndex: 0,
      enabledModules: [],
      fieldErrors: {},
      progress: null,
      multiFormConfig: false,
      showTunnel: false,
      showSite: true,
      showProbe: false,
      showBlackbox: false,
      showLoadsheddingarea: false,
      showFortigate: false,
      showHostAvailability: false,
      showNetworkDevice: false,
      configSelection: true,
      orgOptions: [],
      configOptions: ["Probe Site Configuration", "Central Monitoring Configuration", "Site Only Configuration" ],
      selectedOrg: "",
      selectedConfig: "",
      newSite: {
        code: "",
        description: "",
        mgmt_loopback_address: "",
        mgmt_loopback_address_port: 443,
        country: "",
        region: "",
        org: null,
        division: null,
        api_key: "",
        api_address: null,
        api_port: null,
        scrape_interval: 60,
        scrape_timeout: 30,
        device_name: "",
        geohash: "",
        in_maintenance: false,
      },
      canAddSite: false,
      errorMessages: errorMessages,
      countryChoices: {
        "ZA": "South Africa",
        "BW": "Botswana",
        "NA": "Namibia",
        "SZ": "Swaziland",
        "ZM": "Zimbabwe",
        "LS": "Lesotho",
        "ET": "Ethiopia",
        "KN": "Kenya",
        "NG": "Nigeria",
        "SH": "Saint Helena",
      },
      regionsByCountry: {
        "ZA": [
          { code: "EC", name: "Eastern Cape" },
          { code: "FS", name: "Free State" },
          { code: "GT", name: "Gauteng" },
          { code: "NL", name: "KwaZulu-Natal" },
          { code: "LP", name: "Limpopo" },
          { code: "MP", name: "Mpumalanga" },
          { code: "NC", name: "Northern Cape" },
          { code: "NW", name: "North-West" },
          { code: "WC", name: "Western Cape" }
        ],
        "BW": [
          { code: "CE", name: "Central" },
          { code: "CH", name: "Chobe" },
          { code: "GH", name: "Ghanzi" },
          { code: "KG", name: "Kgalagadi" },
          { code: "KL", name: "Kgatleng" },
          { code: "KW", name: "Kweneng" },
          { code: "NE", name: "North-East" },
          { code: "NW", name: "North-West" },
          { code: "SE", name: "South-East" },
          { code: "SO", name: "Southern" }
        ],
        "NA": [
          { code: "ER", name: "Erongo" },
          { code: "HA", name: "Hardap" },
          { code: "KA", name: "Karas" },
          { code: "KE", name: "Kavango East" },
          { code: "KW", name: "Kavango West" },
          { code: "KH", name: "Khomas" },
          { code: "KU", name: "Kunene" },
          { code: "OW", name: "Ohangwena" },
          { code: "OH", name: "Omaheke" },
          { code: "OS", name: "Omusati" },
          { code: "ON", name: "Oshana" },
          { code: "OT", name: "Oshikoto" },
          { code: "OD", name: "Otjozondjupa" },
          { code: "CA", name: "Zambezi" }
        ],
        "SZ": [
          { code: "HH", name: "Hhohho" },
          { code: "LU", name: "Lubombo" },
          { code: "MA", name: "Manzini" },
          { code: "SH", name: "Shiselweni" }
        ],
        "ZM": [
          { code: "BU", name: "Bulawayo" },
          { code: "HA", name: "Harare" },
          { code: "MA", name: "Manicaland" },
          { code: "MC", name: "Mashonaland Central" },
          { code: "ME", name: "Mashonaland East" },
          { code: "MW", name: "Mashonaland West" },
          { code: "MV", name: "Masvingo" },
          { code: "MN", name: "Matabeleland North" },
          { code: "MS", name: "Matabeleland South" },
          { code: "MI", name: "Midlands" }
        ],
        "LS": [
          { code: "BB", name: "Butha-Buthe" },
          { code: "LE", name: "Leribe" },
          { code: "BE", name: "Berea" },
          { code: "MS", name: "Maseru" },
          { code: "MF", name: "Mafeteng" },
          { code: "MH", name: "Mohales Hoek" },
          { code: "QT", name: "Quthing" },
          { code: "QN", name: "Qachas Nek" },
          { code: "MK", name: "Mokhotlong" },
          { code: "TT", name: "Thaba Tseka" }
        ],
        "ET": [
            { code: "AA", name: "Addis Ababa" },
            { code: "AF", name: "Afar" },
            { code: "AM", name: "Amhara" },
            { code: "BG", name: "Benishanul-Gumuz" },
            { code: "CE", name: "Central Ethiopia" },
            { code: "DD", name: "Dire Dawa" },
            { code: "GB", name: "Gambela" },
            { code: "HR", name: "Harari" },
            { code: "OR", name: "Oromia" },
            { code: "SD", name: "Sidama" },
            { code: "SM", name: "Somali" },
            { code: "SR", name: " South Ethiopia" },
            { code: "SW", name: "South West Ethiopia" },
            { code: "TG", name: "Tigray" },
        ],
        "NG": [
            { code: "AB", name: "Abia" },
            { code: "AD", name: "Adamawa" },
            { code: "AK", name: "Akwa Ibom" },
            { code: "AN", name: "Anambra" },
            { code: "BA", name: "Bauchi" },
            { code: "BY", name: "Bayelsa" },
            { code: "BE", name: "Benue" },
            { code: "BO", name: "Borno" },
            { code: "CR", name: "Cross River" },
            { code: "DE", name: "Delta" },
            { code: "EB", name: "Ebonyi" },
            { code: "ED", name: "Edo" },
            { code: "EK", name: "Ekiti" },
            { code: "EN", name: "Enugu" },
            { code: "FC", name: "Federal Capital Territory" },
            { code: "GO", name: "Gombe" },
            { code: "IM", name: "Imo" },
            { code: "JI", name: "Jigawa" },
            { code: "KD", name: "Kaduna" },
            { code: "KN", name: "Kano" },
            { code: "KT", name: "Katsina" },
            { code: "KE", name: "Kebbi" },
            { code: "KO", name: "Kogi" },
            { code: "KW", name: "Kwara" },
            { code: "LA", name: "Lagos" },
            { code: "NA", name: "Nasarawa" },
            { code: "NI", name: "Niger" },
            { code: "OG", name: "Ogun" },
            { code: "ON", name: "Ondo" },
            { code: "OS", name: "Osun" },
            { code: "OY", name: "Oyo" },
            { code: "PL", name: "Plateau" },
            { code: "RI", name: "Rivers" },
            { code: "SO", name: "Sokoto" },
            { code: "TA", name: "Taraba" },
            { code: "YO", name: "Yobe" },
            { code: "ZA", name: "Zamfara" },
        ],
        "KN": [
            { code: "MB", name: "Mombasa" },
            { code: "KW", name: "Kwale" },
            { code: "KL", name: "Kilifi" },
            { code: "TR", name: "Tana River" },
            { code: "LM", name: "Lamu" },
            { code: "TT", name: "Taita-Taveta" },
            { code: "GR", name: "Garissa" },
            { code: "WJ", name: "Wajir" },
            { code: "MD", name: "Mandera" },
            { code: "MS", name: "Marsabit" },
            { code: "IS", name: "Isiolo" },
            { code: "ME", name: "Meru" },
            { code: "TN", name: "Tharaka-Nithi" },
            { code: "EM", name: "Embu" },
            { code: "KT", name: "Kitui" },
            { code: "MH", name: "Machakos" },
            { code: "MK", name: "Makueni" },
            { code: "ND", name: "Nyandaru" },
            { code: "NY", name: "Nyeri" },
            { code: "KR", name: "Kirinyaga" },
            { code: "MR", name: "Murang'a" },
            { code: "KM", name: "Kiambu" },
            { code: "TK", name: "Turkana" },
            { code: "WP", name: "West Pokot" },
            { code: "SB", name: "Samburu" },
            { code: "TA", name: "Trans-Nzoia" },
            { code: "UG", name: "Uasin Gishu" },
            { code: "EL", name: "Elgeyo-Marakwet" },
            { code: "NN", name: "Nandi" },
            { code: "BR", name: "Baringo" },
            { code: "LK", name: "Laikipia" },
            { code: "NK", name: "Nakuru" },
            { code: "NR", name: "Narok" },
            { code: "KJ", name: "Kajiado" },
            { code: "KC", name: "Kericho" },
            { code: "BO", name: "Bornet" },
            { code: "KK", name: "Kakamega" },
            { code: "VH", name: "Vihiga" },
            { code: "BU", name: "Bungoma" },
            { code: "BS", name: "Busia" },
            { code: "SY", name: "Siaya" },
            { code: "KI", name: "Kisumu" },
            { code: "HB", name: "Homa Bay" },
            { code: "MI", name: "Migori" },
            { code: "KS", name: "Kisii" },
            { code: "NM", name: "Nyamira" },
            { code: "NB", name: "Nairobi" },
        ],
        "SH": [
            { code: "AC", name: "Ascension Island" },
            { code: "SHI", name: "Saint Helena" },
            { code: "TR", name: "Tristan da Cunha" },
        ]
      },
      filteredRegions: [],
      organizations: [],
      divisions: [],
      centralMonitoringConfig: ['Site', 'Tunnel','Endpoint Availability', 'Host Availability', 'Network Device', 'Fortigate',  'Loadsheddingarea'],
      probeSiteConfig: ['Site', 'Tunnel', 'Probe', 'Endpoint Availability', 'Host Availability', 'Network Device', 'Fortigate', 'Loadsheddingarea'],
      configTutorialSteps: [
        { element: '#site-config-org', action: 'select', value: 'Tutorial Organisation', message: 'Select the organisation this site belongs to.' },
        { element: '#site-config-type', action: 'select', value: 'Probe Site Configuration', message: 'Choose a site configuration type: Probe Site, Central Monitoring, or Site Only.' },
      ],
      siteTutorialSteps: [
        { element: '#code', action: 'type', value: 'ABC-001', message: 'Enter a short site code to identify this site.' },
        { element: '#description', action: 'type', value: 'Tutorial Site Description', message: 'Enter a full description of the site location.' },
        { element: '#country', action: 'display', message: 'Select the country where this site is located.' },
        { element: '#region', action: 'display', message: 'Select the region within the chosen country.' },
        { element: '#org', action: 'display', message: 'Organisation is pre-filled from your configuration selection.' },
        { element: '#division', action: 'display', message: 'Select the division this site belongs to.' },
        { element: '#mgmt_loopback_address', action: 'type', value: '10.0.0.1', message: 'Enter the management IP address.' },
        { element: '#submit-site-btn', action: 'display', message: 'Click Add Site to create the site and proceed to the next step.' },
      ],
    }
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  computed: {
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    orgDivisions(){
      const orgId = this.organizations.find((org) => org.name === this.selectedOrg)?.id;
      return this.divisions.filter((division) => orgId === division.org)
    },
    configOrganizations(){
      const org = this.organizations.find((org) => org.name === this.selectedOrg);
      return [org];
    },
    user() {
      return useStore.getUser();
    },
    countryOptions() {
      return Object.entries(this.countryChoices).map(([code, name]) => ({ id: code, name }));
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true 
    },
    selectedConfig: {
      handler(value) {
        if (value) {
          this.fetchModules(value);
        }
      },
      immediate: true
    }
  },
  methods: {
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    validateSiteCofigData(){
      this.fieldErrors = {};
      if (this.selectedOrg === ""){
        this.fieldErrors['selectedOrg'] = "Please select an organisation";
      }
      if (this.selectedConfig === ""){
        this.fieldErrors['selectedConfig'] = "Please select a configuration type";
      }
    },
    async startTutorial() {
      if (this.configSelection) {
        const originalOrgOptions = [...this.orgOptions];
        const originalSelectedOrg = this.selectedOrg;
        const originalSelectedConfig = this.selectedConfig;
        this.orgOptions = ['Tutorial Organisation'];
        await this._start(this.configTutorialSteps, () => {
          this.orgOptions = originalOrgOptions;
          this.selectedOrg = originalSelectedOrg;
          this.selectedConfig = originalSelectedConfig;
        });
      } else {
        const originalSite = { ...this.newSite };
        const steps = [...this.siteTutorialSteps];
        if (this.selectedConfig !== 'Site Only Configuration') {
          steps.unshift({ element: '#site-progress-tracker', action: 'display', message: 'This tracker shows your progress through the multi-step site setup.' });
        }
        await this._start(steps, () => {
          this.newSite = { ...originalSite };
        });
      }
    },
    async fetchModules(config){
      try {
        const response = await getAllModules();
        const permissionNameMap = {
          'Blackbox': 'Blackbox',
          'Loadsheddingarea': 'loadshedding area',
          'Probe': 'probe',
          'Tunnel': 'tunnel',
          'Site': 'site',
          'Blackbox': 'Blackbox',
          'Fortigate': 'fortigate',
          'HostAvailability': 'Host Availability',
          'NetworkDevice': 'Network Device',
        };

        const displayNameMap = {
          'Blackbox': 'Endpoint Availability',
          'HostAvailability': 'Host Availability',
          'NetworkDevice': 'Network Device',
        };
        

        const transformedModules = response
          .map(mod => {
            const displayName = displayNameMap[mod.name] || mod.name;
            const permissionName = permissionNameMap[mod.name] || mod.name.toLowerCase();
            const permissionKey = `Can add ${permissionName}`;

            if (useStore.hasPermission(permissionKey)) {
              return {
                ...mod,
                name: displayName
              };
            } else {
              return null;
            }
          })
          .filter(Boolean);

        switch(config) {
          case "Probe Site Configuration":
            const mods1 = new Set(transformedModules
              .filter((mod) => mod.enabled)
              .map((item) => item.name));
            if(!this.user?.is_staff) this.enabledModules = this.probeSiteConfig.filter((item => mods1.has(item)))
            else this.enabledModules = this.probeSiteConfig;
            break;
            
          case 'Central Monitoring Configuration': 
            const mods2 = new Set(transformedModules
              .filter((mod) => mod.enabled)
              .map((item) => item.name));
            if(!this.user?.is_staff) this.enabledModules = this.centralMonitoringConfig.filter((item => mods2.has(item)))
            else this.enabledModules = this.centralMonitoringConfig;
            break;
            
          default:
            this.enabledModules = ['Site'];
        }
        
      } catch (error) {
        console.error("Error fetching modules")
      }
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddSite = useStore.hasPermission("Can add site");
      }
    },
    validateFields(fields, fieldsToCheck) {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
      }
      return errors;
    },
    async addSite() {
      const requiredFields = {
        code: 'Site Code',
        description: 'Description',
        mgmt_loopback_address: 'Management IP Address',
        country: 'Country',
        region: 'Region',
        org: 'Organization',
        division: 'Division',
      };

      this.fieldErrors = {}

      const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !this.newSite[key])
        .map(([label, ]) => label);

      this.fieldErrors = this.validateFields(this.newSite, missingFields)
      if (Object.keys(this.fieldErrors).length > 0) return;

      try {
        // Create a copy of the data and ensure org and division are numbers
        const siteData = {
          ...this.newSite,
          name: `${this.newSite.description} (${this.newSite.code})`,
          description_abbreviate: this.newSite.description.slice(0, 50),
          org: parseInt(this.newSite.org),
          division: parseInt(this.newSite.division),
          mgmt_loopback_address_port: parseInt(this.newSite.mgmt_loopback_address_port) || 443,
          api_port: parseInt(this.newSite.api_port) || null,
          scrape_interval: parseInt(this.newSite.scrape_interval) || 60,
          scrape_timeout: parseInt(this.newSite.scrape_timeout) || 30
        };

        const validation = this.tenant.validateSchemaForOperation();

        if (!validation.valid) {
          this.error = "Malformed request. Failed to create site!";
          this.errorCode = "422!";
          return;
        }
        this.submittedSite = await addSite(siteData, validation.schemaName);

        if (this.selectedConfig === "Site Only Configuration") {
          this.progress = 4;
          const selectedOrg = this.tenant.organizations.value.find(org => org.id === siteData.org);
          this.tenant.switchOrganization(selectedOrg.id, () => {});
          setTimeout(() => {
            this.$router.push('/sites');
          }, 1000);
        } else {
          this.handleNextModule();
        }

      } catch (error) {

        this.fieldErrors = {}; 
        const errData = error?.response?.data;

        for (const element in errData){
          const fieldError = errData?.[element];
          if (fieldError?.[0]) {
            this.fieldErrors[element] = fieldError[0]; 
          }
        }

        console.error('Error adding site:', error);
      }
    },
    async handleNextModule(data) {
      let currentModule = this.enabledModules[this.currentModuleIndex];
      if (currentModule == 'Endpoint Availability') currentModule = "Blackbox"
      if (currentModule == 'Host Availability') currentModule = "HostAvailability"
      if (currentModule == 'Network Device') currentModule = "NetworkDevice"

      if (currentModule === 'Probe' && data) {
        this.submittedProbe = data;  
      }

      // Hide current module
      if (currentModule) {
        this[`show${currentModule}`] = false;
      }

      // Move to the next module
      this.currentModuleIndex += 1;
      let nextModule = this.enabledModules[this.currentModuleIndex];
      if (nextModule == 'Endpoint Availability') nextModule = "Blackbox"
      if (nextModule == 'Host Availability') nextModule = "HostAvailability"
      if (nextModule == 'Network Device') nextModule = "NetworkDevice"


      if (nextModule) {
        this[`show${nextModule}`] = true;

        this.progress += 1;
      } else {
        const selectedOrg = this.tenant.organizations.value.find(
          org => org.id === this.newSite.org
        );
        if (selectedOrg) {
          await this.tenant.switchOrganization(selectedOrg.id, () => {});
        }
        this.$router.push('/sites');

      }
    },
    filterRegions() {
      this.filteredRegions = this.regionsByCountry[this.newSite.country] || [];
    },
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs;
        this.orgOptions = this.organizations.filter((org) => org.name !== 'Splitpoint').map((org) => org.name);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },
    async fetchDivisions() {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to fetch divisions!";
          this.errorCode = "422!";
          return;
        }
        const divisions = await getAllDivisions(validation.schemaName);
        this.divisions = divisions;
      } catch (error) {
        console.error("Error fetching divisions:", error);
      }
    },
    async manageConfig(){
      this.validateSiteCofigData()
      if (Object.keys(this.fieldErrors).length > 0) return;
      if(this.selectedConfig !== "Site Only Configuration"  && this.enabledModules.length === 1){
        this.selectedConfig = "Site Only Configuration";
      }
      if (this.selectedOrg && this.selectedConfig ){
        const selectedOrgObj =  this.organizations.find((org) => org.name.toLowerCase() === this.selectedOrg.toLowerCase());
        this.newSite.org = selectedOrgObj?.id;
        if (isStaffOrServiceProvider() && selectedOrgObj?.schema_name) {
          await this.tenant.switchOrganization(selectedOrgObj.id, () => {});
          await this.$router.replace({
            query: { 
              ...this.$route.query,
              client: selectedOrgObj.schema_name 
            }
          });
        }
      }
      await this.fetchDivisions();
      this.configSelection = false;
    },
  },
  mounted() {
    this.fetchOrganizations();
  },
};
</script> 

<style scoped></style>
